import React, { useEffect, useState } from 'react';

const RoleModifyModal = ({ isOpen, onClose, onSave, item }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    skills: [],
    link: '',
    categories: [],
    programming_languages: [],
    frameworks_and_tools: [],
    job_focus: 'Other',
    visa_status: 'Not Specified',
    min_education_level: 'Bachelor',
    min_graduation_year: null,
    max_graduation_year: null,
    requires_security_clearance: false,
    work_location: 'On-site',
    internship_season: 'Summer',
    duration_weeks: null,
    housing_status: 'Not Specified',
    required_gpa: null,
    application_deadline: null,
    company_size: null,
  });

  const [newSkill, setNewSkill] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [newProgrammingLanguage, setNewProgrammingLanguage] = useState('');
  const [newFramework, setNewFramework] = useState('');

  // Initialize form data when modal opens
  useEffect(() => {
    if (isOpen && item) {
      const role = item.role || item;
      setFormData({
        name: role.name || '',
        description: role.description || '',
        skills: role.skills || [],
        link: role.link || '',
        categories: role.categories || [],
        programming_languages: role.programming_languages || [],
        frameworks_and_tools: role.frameworks_and_tools || [],
        job_focus: role.job_focus || 'Other',
        visa_status: role.visa_status || 'Not Specified',
        min_education_level: role.min_education_level || 'Bachelor',
        min_graduation_year: role.min_graduation_year || null,
        max_graduation_year: role.max_graduation_year || null,
        requires_security_clearance: role.requires_security_clearance || false,
        work_location: role.work_location || 'On-site',
        internship_season: role.internship_season || 'Summer',
        duration_weeks: role.duration_weeks || null,
        housing_status: role.housing_status || 'Not Specified',
        required_gpa: role.required_gpa || null,
        application_deadline: role.application_deadline || null,
        company_size: role.company_size || null,
      });
    }
  }, [isOpen, item]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const addArrayItem = (field, newItem, setNewItem) => {
    if (newItem.trim()) {
      setFormData((prev) => ({
        ...prev,
        [field]: [...prev[field], newItem.trim()],
      }));
      setNewItem('');
    }
  };

  const removeArrayItem = (field, index) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const handleSave = () => {
    onSave(formData);
    onClose();
  };

  const handleClose = () => {
    onClose();
    // Reset form data
    setFormData({
      name: '',
      description: '',
      skills: [],
      link: '',
      categories: [],
      programming_languages: [],
      frameworks_and_tools: [],
      job_focus: 'Other',
      visa_status: 'Not Specified',
      min_education_level: 'Bachelor',
      min_graduation_year: null,
      max_graduation_year: null,
      requires_security_clearance: false,
      work_location: 'On-site',
      internship_season: 'Summer',
      duration_weeks: null,
      housing_status: 'Not Specified',
      required_gpa: null,
      application_deadline: null,
      company_size: null,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-9999 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500/75 transition-opacity"
          onClick={handleClose}
        ></div>

        <div className="relative transform overflow-hidden rounded-xl bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-5xl">
          {/* Header */}
          <div className="px-4 py-6 sm:px-6 lg:px-8 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Modify Role Details</h1>
                <p className="mt-1 text-sm text-gray-600">
                  Update role information and requirements for better tracking.
                </p>
              </div>
              <button
                type="button"
                className="inline-flex justify-center items-center size-8 text-sm font-semibold rounded-lg border border-transparent text-gray-800 hover:bg-gray-100"
                onClick={handleClose}
              >
                <span className="sr-only">Close</span>
                <svg
                  className="shrink-0 size-4"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m18 6-12 12" />
                  <path d="m6 6 12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="px-4 py-6 sm:px-6 lg:px-8 max-h-[70vh] overflow-y-auto">
            <form>
              <div className="space-y-8">
                {/* Basic Information Section */}
                <div>
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">Basic Information</h2>
                  <div className="space-y-4">
                    {/* Note: Role name is edited inline on the table row */}

                    {/* Description */}
                    <div>
                      <label
                        htmlFor="role-description"
                        className="block mb-2 text-sm text-gray-700 font-medium"
                      >
                        Description
                      </label>
                      <textarea
                        id="role-description"
                        rows={4}
                        className="py-2.5 sm:py-3 px-4 block w-full border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        placeholder="Describe the role responsibilities, requirements, and expectations..."
                      />
                    </div>

                    {/* Job Posting Link */}
                    <div>
                      <label
                        htmlFor="role-link"
                        className="block mb-2 text-sm text-gray-700 font-medium"
                      >
                        Job Posting Link
                      </label>
                      <input
                        type="url"
                        id="role-link"
                        className="py-2.5 sm:py-3 px-4 block w-full border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                        value={formData.link}
                        onChange={(e) => handleInputChange('link', e.target.value)}
                        placeholder="https://company.com/careers/internship"
                      />
                    </div>
                  </div>
                </div>

                {/* Skills & Technologies Section */}
                <div>
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">
                    Skills & Technologies
                  </h2>
                  <div className="space-y-6">
                    {/* Skills */}
                    <div>
                      <label className="block mb-2 text-sm text-gray-700 font-medium">
                        Required Skills
                      </label>
                      <div className="flex gap-x-2 mb-3">
                        <input
                          type="text"
                          className="py-2.5 px-4 block w-full border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500"
                          value={newSkill}
                          onChange={(e) => setNewSkill(e.target.value)}
                          placeholder="e.g., React, Problem Solving, Communication"
                          onKeyPress={(e) =>
                            e.key === 'Enter' &&
                            e.preventDefault() &&
                            addArrayItem('skills', newSkill, setNewSkill)
                          }
                        />
                        <button
                          type="button"
                          className="py-2.5 px-4 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg border border-transparent bg-blue-600 text-white hover:bg-blue-700 focus:outline-hidden focus:bg-blue-700 disabled:opacity-50 disabled:pointer-events-none"
                          onClick={() => addArrayItem('skills', newSkill, setNewSkill)}
                        >
                          Add
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {formData.skills.map((skill, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center gap-x-1.5 py-1.5 px-3 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {skill}
                            <button
                              type="button"
                              onClick={() => removeArrayItem('skills', index)}
                              className="inline-flex items-center justify-center size-4 rounded-full text-blue-800 hover:bg-blue-200"
                            >
                              <svg
                                className="shrink-0 size-3"
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="m18 6-12 12" />
                                <path d="m6 6 12 12" />
                              </svg>
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Programming Languages */}
                    <div>
                      <label className="block mb-2 text-sm text-gray-700 font-medium">
                        Programming Languages
                      </label>
                      <div className="flex gap-x-2 mb-3">
                        <input
                          type="text"
                          className="py-2.5 px-4 block w-full border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500"
                          value={newProgrammingLanguage}
                          onChange={(e) => setNewProgrammingLanguage(e.target.value)}
                          placeholder="e.g., Python, JavaScript, Java"
                          onKeyPress={(e) =>
                            e.key === 'Enter' &&
                            e.preventDefault() &&
                            addArrayItem(
                              'programming_languages',
                              newProgrammingLanguage,
                              setNewProgrammingLanguage
                            )
                          }
                        />
                        <button
                          type="button"
                          className="py-2.5 px-4 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg border border-transparent bg-blue-600 text-white hover:bg-blue-700 focus:outline-hidden focus:bg-blue-700 disabled:opacity-50 disabled:pointer-events-none"
                          onClick={() =>
                            addArrayItem(
                              'programming_languages',
                              newProgrammingLanguage,
                              setNewProgrammingLanguage
                            )
                          }
                        >
                          Add
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {formData.programming_languages.map((lang, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center gap-x-1.5 py-1.5 px-3 rounded-full text-xs font-medium bg-green-100 text-green-800"
                          >
                            {lang}
                            <button
                              type="button"
                              onClick={() => removeArrayItem('programming_languages', index)}
                              className="inline-flex items-center justify-center size-4 rounded-full text-green-800 hover:bg-green-200"
                            >
                              <svg
                                className="shrink-0 size-3"
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="m18 6-12 12" />
                                <path d="m6 6 12 12" />
                              </svg>
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Role Requirements Section */}
                <div>
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">Role Requirements</h2>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                    {/* Job Focus */}
                    <div>
                      <label
                        htmlFor="job-focus"
                        className="block mb-2 text-sm text-gray-700 font-medium"
                      >
                        Job Focus
                      </label>
                      <select
                        id="job-focus"
                        className="py-2.5 px-4 pe-9 block w-full border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                        value={formData.job_focus}
                        onChange={(e) => handleInputChange('job_focus', e.target.value)}
                      >
                        <option value="Frontend">Frontend</option>
                        <option value="Backend">Backend</option>
                        <option value="Full Stack">Full Stack</option>
                        <option value="Mobile">Mobile</option>
                        <option value="DevOps">DevOps</option>
                        <option value="ML/AI">ML/AI</option>
                        <option value="Data Engineering">Data Engineering</option>
                        <option value="Security">Security</option>
                        <option value="Embedded Systems">Embedded Systems</option>
                        <option value="Cloud">Cloud</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    {/* Visa Status */}
                    <div>
                      <label
                        htmlFor="visa-status"
                        className="block mb-2 text-sm text-gray-700 font-medium"
                      >
                        Visa Status
                      </label>
                      <select
                        id="visa-status"
                        className="py-2.5 px-4 pe-9 block w-full border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                        value={formData.visa_status}
                        onChange={(e) => handleInputChange('visa_status', e.target.value)}
                      >
                        <option value="US Citizens Only">US Citizens Only</option>
                        <option value="Accepts OPT/CPT">Accepts OPT/CPT</option>
                        <option value="Sponsors H1B">Sponsors H1B</option>
                        <option value="Not Specified">Not Specified</option>
                      </select>
                    </div>

                    {/* Work Location */}
                    <div>
                      <label
                        htmlFor="work-location"
                        className="block mb-2 text-sm text-gray-700 font-medium"
                      >
                        Work Location
                      </label>
                      <select
                        id="work-location"
                        className="py-2.5 px-4 pe-9 block w-full border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                        value={formData.work_location}
                        onChange={(e) => handleInputChange('work_location', e.target.value)}
                      >
                        <option value="On-site">On-site</option>
                        <option value="Hybrid">Hybrid</option>
                        <option value="Remote">Remote</option>
                        <option value="Flexible">Flexible</option>
                      </select>
                    </div>

                    {/* Internship Season */}
                    <div>
                      <label
                        htmlFor="internship-season"
                        className="block mb-2 text-sm text-gray-700 font-medium"
                      >
                        Internship Season
                      </label>
                      <select
                        id="internship-season"
                        className="py-2.5 px-4 pe-9 block w-full border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                        value={formData.internship_season}
                        onChange={(e) => handleInputChange('internship_season', e.target.value)}
                      >
                        <option value="Summer">Summer</option>
                        <option value="Fall">Fall</option>
                        <option value="Winter">Winter</option>
                        <option value="Spring">Spring</option>
                        <option value="Year-round">Year-round</option>
                      </select>
                    </div>

                    {/* Housing Status */}
                    <div>
                      <label
                        htmlFor="housing-status"
                        className="block mb-2 text-sm text-gray-700 font-medium"
                      >
                        Housing Status
                      </label>
                      <select
                        id="housing-status"
                        className="py-2.5 px-4 pe-9 block w-full border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                        value={formData.housing_status}
                        onChange={(e) => handleInputChange('housing_status', e.target.value)}
                      >
                        <option value="Housing Provided">Housing Provided</option>
                        <option value="Housing Stipend">Housing Stipend</option>
                        <option value="No Housing Support">No Housing Support</option>
                        <option value="Not Specified">Not Specified</option>
                      </select>
                    </div>

                    {/* Duration */}
                    <div>
                      <label
                        htmlFor="duration-weeks"
                        className="block mb-2 text-sm text-gray-700 font-medium"
                      >
                        Duration (weeks)
                      </label>
                      <input
                        type="number"
                        id="duration-weeks"
                        className="py-2.5 px-4 block w-full border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                        value={formData.duration_weeks || ''}
                        onChange={(e) =>
                          handleInputChange(
                            'duration_weeks',
                            e.target.value ? parseInt(e.target.value) : null
                          )
                        }
                        placeholder="12"
                      />
                    </div>

                    {/* Required GPA */}
                    <div>
                      <label
                        htmlFor="required-gpa"
                        className="block mb-2 text-sm text-gray-700 font-medium"
                      >
                        Required GPA
                      </label>
                      <input
                        type="number"
                        id="required-gpa"
                        step="0.1"
                        min="0"
                        max="4.0"
                        className="py-2.5 px-4 block w-full border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                        value={formData.required_gpa || ''}
                        onChange={(e) =>
                          handleInputChange(
                            'required_gpa',
                            e.target.value ? parseFloat(e.target.value) : null
                          )
                        }
                        placeholder="3.0"
                      />
                    </div>
                  </div>

                  {/* Application Deadline */}
                  <div className="mt-4">
                    <label
                      htmlFor="application-deadline"
                      className="block mb-2 text-sm text-gray-700 font-medium"
                    >
                      Application Deadline
                    </label>
                    <input
                      type="date"
                      id="application-deadline"
                      className="py-2.5 px-4 block w-full sm:w-auto border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                      value={formData.application_deadline || ''}
                      onChange={(e) => handleInputChange('application_deadline', e.target.value)}
                    />
                  </div>

                  {/* Security Clearance Checkbox */}
                  <div className="mt-6">
                    <div className="flex">
                      <div className="flex">
                        <input
                          id="security-clearance"
                          name="security-clearance"
                          type="checkbox"
                          className="shrink-0 mt-0.5 border-gray-300 rounded-sm text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                          checked={formData.requires_security_clearance}
                          onChange={(e) =>
                            handleInputChange('requires_security_clearance', e.target.checked)
                          }
                        />
                      </div>
                      <div className="ms-3">
                        <label htmlFor="security-clearance" className="text-sm text-gray-600">
                          Requires Security Clearance
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="px-4 py-4 sm:px-6 lg:px-8 border-t border-gray-200">
            <div className="flex justify-end gap-x-2">
              <button
                type="button"
                className="py-2.5 px-4 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-800 shadow-xs hover:bg-gray-50 focus:outline-hidden focus:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none"
                onClick={handleClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="py-2.5 px-4 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg border border-transparent bg-blue-600 text-white hover:bg-blue-700 focus:outline-hidden focus:bg-blue-700 disabled:opacity-50 disabled:pointer-events-none"
                onClick={handleSave}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoleModifyModal;
